import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
  FormState,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, Callout, Combobox, Input, Radio, RowsSkeletonLoader } from '@node-space/ui-components'
import { RequestError } from 'components/Errors/RequestError'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useCustomersQuery } from 'hooks/queries/Customers/useCustomersQuery'
import { useGetPayoutsTransferWalletsQuery } from 'hooks/queries/useGetPayoutsTransferWalletsQuery'
import { CustomerModels } from 'pages/Customers/helpers/types'
import { isCryptoCustomerWallet } from 'pages/Customers/helpers/utils'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { Customer } from 'types/customers'
import { SourceWalletTypes } from 'types/move/move'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { MovePayload, TransferStep } from '../../../NestedCustomers/types'
import { useFormError } from './hooks/useFormError'
import { useFormHandlers } from './hooks/useFormHandlers'
import { useFormLabels } from './hooks/useFormLabels'
import { useFormOptions } from './hooks/useFormOptions'
import { useSourceWallet } from './hooks/useSourceWallet'
import { useValidateFormAmount } from './hooks/useValidateFormAmount'
import { getFieldsToValidate } from './utils'
import { useDebounce } from '@node-space/hooks'
import { SEARCH_INPUT_DEBOUNCE_TIME } from 'constants/General'
import { stripSpecialCharacters } from 'pages/Contacts/utils/utils'

interface MoveFormProps {
  formState?: FormState<MovePayload>
  formValues?: MovePayload
  clearErrors?: UseFormClearErrors<MovePayload>
  handleClose: () => void
  register?: UseFormRegister<MovePayload>
  setError?: UseFormSetError<MovePayload>
  setTransferStep: React.Dispatch<React.SetStateAction<TransferStep>>
  setValue?: UseFormSetValue<MovePayload>
  trigger?: UseFormTrigger<MovePayload>
}

export const MoveForm = ({
  formState,
  formValues,
  clearErrors,
  handleClose,
  register,
  setError,
  setTransferStep,
  setValue,
  trigger,
}: MoveFormProps) => {
  const { t } = useTranslation()
  const [walletAction, setWalletAction] = useContext(WalletActionContext)
  const sourceWallet = useSourceWallet()

  const { meta } = walletAction || {}
  const { customerModel } = meta || {}
  const { accountCapabilities } = useAccountsContext()
  const { isEpcWalletInternalTransfersCapabilityEnabled } = accountCapabilities || {}

  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchTerm = useDebounce(searchQuery, SEARCH_INPUT_DEBOUNCE_TIME)
 
  const {
    data: { content: customerList } = {},
    isFetching: isFetchingCustomers,
    error: fetchingCustomersError,
  } = useCustomersQuery<Customer>({ size: 1000, search: debouncedSearchTerm })

  const {
    data: transferWallets,
    isFetching: isFetchingTransferWallets,
    error: transferWalletsErrors,
    refetch: fetchTransferWallets,
  } = useGetPayoutsTransferWalletsQuery(
    {
      walletId: sourceWallet?.walletId,
      currencyCode: sourceWallet?.currency,
      walletType: formValues?.walletTransferType,
      customerReference: formValues?.customerReference,
    },
    false
  )

  const { requestError } = useFormError({ fetchingCustomersError, transferWalletsErrors })

  useEffect(() => {
    if (
      formValues?.walletTransferType === WalletTransferType.MAIN ||
      (formValues?.walletTransferType === WalletTransferType.CUSTOMER &&
        formValues?.customerReference)
    ) {
      fetchTransferWallets()
    }
  }, [formValues?.walletTransferType, formValues?.customerReference])

  const isInsufficientBalance = sourceWallet?.balance <= 0
  const isExceedingMaxAmount = Number(formValues?.amount) > sourceWallet?.balance
  
  const isCustomerWalletTransferType =
    formValues?.walletTransferType === WalletTransferType.CUSTOMER
  const isCryptoWallet = isCryptoCustomerWallet(sourceWallet?.currency)
  const isEmbeddedOrRelianceCustomerModel =
    customerModel === CustomerModels.EMBEDDED || customerModel === CustomerModels.RELIANCE
  const isLoading = isFetchingTransferWallets || isFetchingCustomers
  const hasTransferWallets = !!transferWallets?.length

  const {
    customerOptions,
    transferWalletOptions,
    walletTransferTypeOptions,
    selectedSourceWalletOption,
  } = useFormOptions({
    customerList,
    transferWallets,
    sourceWallet,
    isEmbeddedOrRelianceCustomerWallet:
      isEmbeddedOrRelianceCustomerModel && sourceWallet?.walletType === SourceWalletTypes.CUSTOMER,
  })

  const { selectBeneficiaryDropdownPlaceholder, destinationWalletLabel } = useFormLabels({
    transferWalletOptions,
    isCustomerWalletTransferType,
  })

  const isPrincipalWallet = sourceWallet?.walletType === SourceWalletTypes.MAIN
  const isWalletTransferTypeEnabled =
    !isEmbeddedOrRelianceCustomerModel ||
    isPrincipalWallet ||
    (isEmbeddedOrRelianceCustomerModel &&
      isEpcWalletInternalTransfersCapabilityEnabled &&
      !isCryptoWallet &&
      !isPrincipalWallet)

  const { validateAmount } = useValidateFormAmount({ clearErrors, setError })

  const { onCustomerSelection, onDestinationWalletChange, onWalletTransferTypeSelection } =
    useFormHandlers({ setValue })

  const handleContinue = async () => {
    const fieldsToValidate = getFieldsToValidate(formValues?.walletTransferType)
    const isValidDetails = await trigger(fieldsToValidate)

    if (isValidDetails) {
      setWalletAction({
        ...walletAction,
        header: t('customers.reviewTransaction'),
      })
      setTransferStep(TransferStep?.CONFIRMATION)
    }
  }

  const onHandleCustomerSelection = (customerReference: string) => {
    const selectedCustomer = customerOptions?.find(
      customer => customer?.value === customerReference
    )

    onCustomerSelection(customerReference, selectedCustomer?.leftText)
  }

  const onHandleDestinationWalletSelection = (walletReference: string) => {
    const selectedWallet = transferWalletOptions?.find(wallet => wallet?.value === walletReference)

    onDestinationWalletChange(walletReference, selectedWallet?.leftText)
  }

  const isTransferTypeSelected =
    formValues?.walletTransferType === WalletTransferType.MAIN || isCustomerWalletTransferType
  const requiresCustomerReference =
    isCustomerWalletTransferType && !formValues?.customerReference

  const showDestinationWalletCombobox = useMemo(
    () => isTransferTypeSelected && hasTransferWallets && !requiresCustomerReference,
    [isTransferTypeSelected, hasTransferWallets, requiresCustomerReference]
  )

  const showNoWalletsCallout = useMemo(() => {
    if (isLoading || hasTransferWallets) return false
    if (formValues?.walletTransferType === WalletTransferType.MAIN) return !!sourceWallet?.walletId
    return isCustomerWalletTransferType && !!formValues?.customerReference
  }, [isLoading, hasTransferWallets, formValues?.walletTransferType, sourceWallet?.walletId, isCustomerWalletTransferType, formValues?.customerReference])

  const showContent = useMemo(
    () =>
      !isLoading &&
      hasTransferWallets &&
      !!formValues?.destinationWallet &&
      isTransferTypeSelected &&
      !requiresCustomerReference,
    [isLoading, hasTransferWallets, formValues?.destinationWallet, isTransferTypeSelected, requiresCustomerReference]
  )

  const isDisabled = useMemo(() => {
    return isLoading || isInsufficientBalance || isExceedingMaxAmount || showNoWalletsCallout || isFetchingTransferWallets || isFetchingCustomers
  }, [isFetchingTransferWallets, isFetchingCustomers, isInsufficientBalance, isExceedingMaxAmount, showNoWalletsCallout])

  return (
    <Box paddingX={24} width="full" className="my-6 space-y-4 text-gray-100">
      {requestError?.show && <RequestError sentryTraceId={requestError?.sentryTraceId} />}

      <Combobox
        label={t('customers.moveFromWallet')}
        disabled={true}
        defaultValue={sourceWallet?.walletId}
        selectPlaceholder={sourceWallet ? undefined : t('noWallets')}
        id="sourceWallet"
        items={selectedSourceWalletOption}
        loadingInline={isFetchingTransferWallets}
      />
      {isInsufficientBalance ? (
        <Callout variant="error">
          {t('payInsufficient', { currencyCode: sourceWallet?.currency })}
        </Callout>
      ) : (
        <>
          {isWalletTransferTypeEnabled && (
            <Box>
              <Radio
                {...register('walletTransferType')}
                name="walletTransferType"
                horizontal={true}
                onChange={onWalletTransferTypeSelection}
                options={walletTransferTypeOptions}
                value={formValues?.walletTransferType}
                error={formState?.errors?.walletTransferType?.message}
              />
            </Box>
          )}

          {isCustomerWalletTransferType && (
            <Combobox
              {...register('customerReference')}
              label={t('customers.selectCustomer')}
              defaultValue={formValues?.customerReference}
              selectPlaceholder={t('select')}
              id="customerReference"
              items={customerOptions}
              onChange={onHandleCustomerSelection}
              loadingInline={isFetchingCustomers}
              disabled={isFetchingCustomers}
              errorText={formState?.errors?.customerReference?.message}
              onInputChange={(value: string) => {
                setSearchQuery(stripSpecialCharacters(value))
              }}
              loading={isFetchingCustomers}
            />
          )}

          {isFetchingTransferWallets && (
            <Box paddingT={24}>
              <RowsSkeletonLoader rows={6} />
            </Box>
          )}

          {!isFetchingTransferWallets && showDestinationWalletCombobox && (
            <Combobox
              {...register('destinationWallet')}
              key={formValues?.walletTransferType}
              label={destinationWalletLabel}
              selectPlaceholder={selectBeneficiaryDropdownPlaceholder}
              id="destinationWallet"
              items={transferWalletOptions}
              onChange={onHandleDestinationWalletSelection}
              loadingInline={isFetchingTransferWallets}
              errorText={formState?.errors?.destinationWallet?.message}
              disabled={isCustomerWalletTransferType ? !formValues?.customerReference || isFetchingTransferWallets : isFetchingTransferWallets}
              defaultValue={formValues?.destinationWallet}
            />
          )}

          {showNoWalletsCallout && (
            <Callout variant="warning">
              {t('noWallets')}
            </Callout>
          )}

          {showContent && <>
            <Input
            testId="update-amount-input"
            {...register('amount')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setValue('amount', e.target.value)
            }
            label={t('massPayouts.amount')}
            inputMode="text"
            rightSuffixText={sourceWallet?.currency}
            name="amount"
            onBlur={e => validateAmount(e.target.value, sourceWallet)}
            value={formValues?.amount}
            error={!!formState?.errors?.amount}
            errorText={formState?.errors?.amount?.message}
            placeholder="0.00"
          />

          <Input
            testId="update-reference-input"
            {...register('paymentReference')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setValue('paymentReference', e.target.value)
            }
            label={t('massPayouts.paymentReference')}
            placeholder={t('inputPlaceholders.beneficiaryPaymentReferencePlaceHolder')}
            inputMode="text"
            name="paymentReference"
            value={formValues?.paymentReference}
            error={!!formState?.errors?.paymentReference}
            errorText={formState?.errors?.paymentReference?.message}
          />
          </>}
          <Box className="flex justify-between pt-2">
            <Button variant="secondary" onClick={handleClose}>
              {t('cancel')}
            </Button>
            <Button onClick={handleContinue} disabled={isDisabled || requestError?.show}>
              {t('continue')}
            </Button>
          </Box>
        </>
      )}   
    </Box>
  )
}
